// Apolo IDE — usePlanCanvas hook (sprint plan-tab-v1, E2).
//
// Source of truth pro estado de blocks + connections de UM plano ativo.
// Faz autosave debounced (600ms) de positions/sizes em batch e CRUD
// individual de payload/kind via PATCH single.

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  getCanvas,
  createBlock as apiCreateBlock,
  patchBlock as apiPatchBlock,
  deleteBlock as apiDeleteBlock,
  batchUpdateBlocks,
  createConnection as apiCreateConnection,
  deleteConnection as apiDeleteConnection,
} from '../lib/plan/api.js';
import { getBlockKindMeta } from '../lib/plan/blockKinds.js';

const AUTOSAVE_DEBOUNCE_MS = 600;

export function usePlanCanvas(planId) {
  const [blocks, setBlocks] = useState([]);
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [savingState, setSavingState] = useState('idle'); // 'idle' | 'pending' | 'saving' | 'error'
  const pendingUpdates = useRef(new Map()); // id -> {x,y,w,h,z}
  const debounceTimer = useRef(null);

  const reload = useCallback(async () => {
    if (!planId) return;
    setLoading(true);
    setError(null);
    const res = await getCanvas(planId);
    setLoading(false);
    if (!res.ok) { setError(res.error || 'Falha ao carregar canvas'); return; }
    setBlocks(res.data?.blocks ?? []);
    setConnections(res.data?.connections ?? []);
  }, [planId]);

  useEffect(() => { reload(); }, [reload]);

  const flushBatch = useCallback(async () => {
    if (!planId || pendingUpdates.current.size === 0) return;
    const updates = Array.from(pendingUpdates.current.entries()).map(([id, fields]) => ({ id, ...fields }));
    pendingUpdates.current.clear();
    setSavingState('saving');
    const res = await batchUpdateBlocks(planId, updates);
    setSavingState(res.ok ? 'idle' : 'error');
    if (!res.ok) console.warn('[plan-canvas] batch save failed', res.error);
  }, [planId]);

  const scheduleAutosave = useCallback(() => {
    setSavingState('pending');
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(flushBatch, AUTOSAVE_DEBOUNCE_MS);
  }, [flushBatch]);

  // Flush on unmount / planId change
  useEffect(() => {
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      flushBatch();
    };
  }, [flushBatch]);

  const updateBlockLocal = useCallback((id, fields) => {
    setBlocks((prev) => prev.map((b) => (b.id === id ? { ...b, ...fields } : b)));
    const cur = pendingUpdates.current.get(id) || {};
    pendingUpdates.current.set(id, { ...cur, ...fields });
    scheduleAutosave();
  }, [scheduleAutosave]);

  const addBlock = useCallback(async ({ kind, x, y, payload }) => {
    if (!planId) return { ok: false };
    const meta = getBlockKindMeta(kind);
    const block = {
      kind,
      x: Math.round(x ?? 200),
      y: Math.round(y ?? 200),
      w: meta.defaults.w,
      h: meta.defaults.h,
      color: meta.color,
      payload: payload ?? meta.defaults.payload,
    };
    const res = await apiCreateBlock(planId, block);
    if (!res.ok) return { ok: false, error: res.error };
    const created = res.data?.block;
    if (created) setBlocks((prev) => [...prev, created]);
    return { ok: true, block: created };
  }, [planId]);

  const patchBlock = useCallback(async (id, fields) => {
    if (!planId) return { ok: false };
    setBlocks((prev) => prev.map((b) => (b.id === id ? { ...b, ...fields } : b)));
    const res = await apiPatchBlock(planId, id, fields);
    if (!res.ok) {
      console.warn('[plan-canvas] patch failed', res.error);
      return { ok: false, error: res.error };
    }
    const updated = res.data?.block;
    if (updated) setBlocks((prev) => prev.map((b) => (b.id === id ? updated : b)));
    return { ok: true, block: updated };
  }, [planId]);

  const removeBlock = useCallback(async (id) => {
    if (!planId) return { ok: false };
    setBlocks((prev) => prev.filter((b) => b.id !== id));
    setConnections((prev) => prev.filter((c) => c.from_block_id !== id && c.to_block_id !== id));
    const res = await apiDeleteBlock(planId, id);
    return { ok: res.ok };
  }, [planId]);

  const addConnection = useCallback(async ({ from_block_id, to_block_id, label, style }) => {
    if (!planId) return { ok: false };
    const res = await apiCreateConnection(planId, { from_block_id, to_block_id, label, style });
    if (!res.ok) return { ok: false, error: res.error };
    setConnections((prev) => [...prev, res.data.connection]);
    return { ok: true };
  }, [planId]);

  const removeConnection = useCallback(async (id) => {
    if (!planId) return { ok: false };
    setConnections((prev) => prev.filter((c) => c.id !== id));
    await apiDeleteConnection(planId, id);
    return { ok: true };
  }, [planId]);

  return {
    blocks,
    connections,
    loading,
    error,
    savingState,
    reload,
    updateBlockLocal,
    addBlock,
    patchBlock,
    removeBlock,
    addConnection,
    removeConnection,
  };
}
